import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  InfiniteListBase,
  RecordContextProvider,
  useListContext,
  useRecordContext,
  useTimeout,
  useTranslate,
  type Exporter,
} from "ra-core";
import { Plus, RotateCcw } from "lucide-react";
import { Link } from "react-router";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { MobileContent } from "../layout/MobileContent";
import { MobileListHeader } from "../layout/MobileListHeader";
import { InfinitePagination } from "../misc/InfinitePagination";
import { ListHeader } from "../layout/ListHeader";
import type { Company, Contact } from "../types";
import { Avatar } from "./Avatar";
import { contactFullName } from "./contactModel";
import { ContactEmpty } from "./ContactEmpty";

export const ContactList = () => (
  <List
    title={false}
    actions={<ContactListActions />}
    perPage={25}
    sort={{ field: "last_name", order: "ASC" }}
    exporter={exporter}
    pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
  >
    <ContactListLayout />
  </List>
);

const ContactListLayout = () => {
  const { data, isPending, filterValues } = useListContext<Contact>();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  if (!data?.length && !hasFilters) return <ContactEmpty />;

  return <ContactTable />;
};

const ContactTable = () => {
  const translate = useTranslate();

  return (
    <DataTable<Contact>
      rowClick="show"
      className="bg-card"
      empty={
        <div
          className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground"
          role="status"
        >
          {translate("resources.contacts.list.no_results")}
        </div>
      }
    >
      <DataTable.Col source="last_name" label="resources.contacts.fields.name">
        <ContactNameCell />
      </DataTable.Col>
      <DataTable.Col
        source="company_id"
        label="resources.contacts.fields.company_id"
        disableSort
      >
        <ReferenceField
          source="company_id"
          reference="companies"
          link="show"
          className="no-underline"
        >
          <TextField source="name" className="hover:underline" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col
        source="email"
        label="resources.contacts.fields.email"
        disableSort
      />
    </DataTable>
  );
};

const ContactNameCell = () => {
  const record = useRecordContext<Contact>();
  if (!record) return null;
  return (
    <span className="inline-flex items-center gap-2 font-medium">
      <Avatar width={25} height={25} />
      {contactFullName(record)}
    </span>
  );
};

const ContactListActions = () => {
  const translate = useTranslate();
  return (
    <ListHeader
      title={translate("resources.contacts.name", { smart_count: 2 })}
      searchPlaceholder={translate("resources.contacts.filters.search")}
    >
      <ExportButton exporter={exporter} />
      <CreateButton label="resources.contacts.action.new" />
    </ListHeader>
  );
};

export const ContactListMobile = () => (
  <InfiniteListBase
    perPage={25}
    sort={{ field: "last_name", order: "ASC" }}
    exporter={exporter}
    queryOptions={{
      onError: () => {
        /* the content below renders the error itself */
      },
    }}
  >
    <ContactListLayoutMobile />
  </InfiniteListBase>
);

const ContactListLayoutMobile = () => {
  const translate = useTranslate();
  const { isPending, data, error, filterValues } = useListContext<Contact>();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) {
    return (
      <>
        <MobileListHeader
          title={translate("resources.contacts.name", { smart_count: 2 })}
        />
        <MobileContent>
          <ContactEmpty />
        </MobileContent>
      </>
    );
  }

  return (
    <>
      <MobileListHeader
        title={translate("resources.contacts.name", { smart_count: 2 })}
        searchPlaceholder={translate("resources.contacts.filters.search")}
      />
      <MobileContent search>
        <Button asChild className="mb-3 w-full">
          <Link to="/contacts/create">
            <Plus className="size-4" />
            {translate("resources.contacts.action.new")}
          </Link>
        </Button>
        <ContactListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </>
  );
};

const ContactListContentMobile = () => {
  const translate = useTranslate();
  const {
    data: contacts,
    error,
    isPending,
    refetch,
  } = useListContext<Contact>();
  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) return null;
    return (
      <div className="divide-y">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex flex-row items-center gap-4 py-3">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="flex-1">
              <Skeleton className="w-32 h-5 mb-2" />
              <Skeleton className="w-48 h-4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && !contacts) {
    return (
      <div className="p-4 text-center">
        <p className="text-muted-foreground mb-4">
          {translate("resources.contacts.list.error_loading")}
        </p>
        <Button onClick={() => refetch()}>
          <RotateCcw />
          {translate("crm.common.retry")}
        </Button>
      </div>
    );
  }

  if (!contacts.length) {
    return (
      <p className="p-4 text-muted-foreground">
        {translate("resources.contacts.list.no_results")}
      </p>
    );
  }

  return (
    <div className="divide-y">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <Link
            to={`/contacts/${contact.id}/show`}
            className="flex flex-row gap-4 items-center py-3 active:bg-accent/50 transition-colors"
          >
            <Avatar />
            <div className="flex-1 min-w-0">
              <div className="font-medium">{contactFullName(contact)}</div>
              <div className="text-sm text-muted-foreground">
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link={false}
                >
                  <TextField source="name" />
                </ReferenceField>
              </div>
              {contact.email ? (
                <div className="text-sm text-muted-foreground truncate">
                  {contact.email}
                </div>
              ) : null}
            </div>
          </Link>
        </RecordContextProvider>
      ))}
    </div>
  );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords) => {
  const companies = await fetchRelatedRecords<Company>(
    records,
    "company_id",
    "companies",
  );

  const contacts = records.map((contact) => ({
    voornaam: contact.first_name,
    achternaam: contact.last_name ?? "",
    bedrijf:
      contact.company_id != null
        ? (companies[contact.company_id]?.name ?? "")
        : "",
    email: contact.email ?? "",
  }));
  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "contactpersonen");
  });
};
